import { AppError, err, ok, type Result } from '@aibi/shared';
import type {
  AIProvider,
  AIProviderKind,
  AIProviderRequest,
  AIProviderResponse,
} from '../provider.js';

/**
 * Puerto de transporte. El AI Core no conoce URLs, cabeceras ni credenciales: el
 * transporte real (un proxy del servidor que resuelve secretos) se inyecta desde fuera.
 * `endpointId` es un identificador opaco configurado en el servidor, nunca una URL.
 */
export interface AITransportRequest {
  readonly endpointId: string;
  readonly body: Readonly<Record<string, unknown>>;
}

export interface AITransport {
  send(request: AITransportRequest, signal?: AbortSignal): Promise<Result<unknown, AppError>>;
}

/** Proveedores remotos soportados por adaptador (sin SDKs). */
export type RemoteProviderKind = Extract<
  AIProviderKind,
  'openai' | 'azure-openai' | 'azure-ai' | 'anthropic' | 'gemini' | 'n8n' | 'http'
>;

export interface ProviderAdapterConfig {
  readonly id: string;
  readonly kind: RemoteProviderKind;
  readonly displayName?: string;
  /** Modelo o despliegue (informativo); nunca contiene credenciales. */
  readonly model?: string;
  readonly endpointId: string;
  readonly transport: AITransport;
}

/** Mensaje de usuario con el contexto canonico; igual para todos los proveedores. */
const userContent = (request: AIProviderRequest): string =>
  `CONTEXTO:\n${request.serializedContext}\n\nINSTRUCCION:\n${request.userMessage}`;

/** Instrucciones para modelos sin salida estructurada: el esquema va en el prompt. */
const jsonOnlyInstructions = (request: AIProviderRequest): string =>
  `${request.systemInstructions}\nResponde solo con un objeto JSON valido segun este JSON Schema, sin texto adicional:\n${JSON.stringify(request.responseSchema)}`;

/** Cuerpo especifico de cada proveedor, construido solo con datos del AI Core. */
export function buildProviderPayload(
  kind: RemoteProviderKind,
  request: AIProviderRequest,
  model?: string,
): Record<string, unknown> {
  switch (kind) {
    case 'openai':
    case 'azure-openai':
      return {
        ...(kind === 'openai' && model !== undefined ? { model } : {}),
        temperature: 0,
        messages: [
          { role: 'system', content: request.systemInstructions },
          { role: 'user', content: userContent(request) },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: { name: 'ai_model_output', strict: true, schema: request.responseSchema },
        },
      };
    case 'azure-ai':
      // Solo parametros que aceptan todos los modelos de Foundry: los de razonamiento
      // (GPT-5, o-series) rechazan `temperature` y `max_tokens`, y varios del catalogo
      // (DeepSeek-R1, Llama) rechazan `response_format`. La salida se valida igual.
      return {
        ...(model === undefined ? {} : { model }),
        messages: [
          { role: 'system', content: jsonOnlyInstructions(request) },
          { role: 'user', content: userContent(request) },
        ],
      };
    case 'anthropic':
      // `max_tokens` es obligatorio en la API Messages; sin `temperature`, que algunos
      // modelos recientes de Claude no admiten.
      return {
        ...(model === undefined ? {} : { model }),
        max_tokens: 4096,
        system: jsonOnlyInstructions(request),
        messages: [{ role: 'user', content: [{ type: 'text', text: userContent(request) }] }],
      };
    case 'gemini':
      return {
        systemInstruction: {
          parts: [
            {
              text: `${request.systemInstructions}\nJSON Schema:\n${JSON.stringify(request.responseSchema)}`,
            },
          ],
        },
        contents: [{ role: 'user', parts: [{ text: userContent(request) }] }],
        generationConfig: { temperature: 0, responseMimeType: 'application/json' },
      };
    case 'n8n':
    case 'http':
      return {
        requestId: request.requestId,
        locale: request.locale,
        instructions: request.systemInstructions,
        context: request.context,
        message: request.userMessage,
        allowedActions: request.allowedActions,
        responseSchema: request.responseSchema,
      };
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const at = (value: unknown, ...path: (string | number)[]): unknown => {
  let current = value;
  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(current)) return undefined;
      current = current[segment];
    } else {
      if (!isRecord(current)) return undefined;
      current = current[segment];
    }
  }
  return current;
};

/**
 * Modelos sin salida estructurada: quita el razonamiento `<think>...</think>`
 * (DeepSeek-R1), las vallas de codigo y el texto alrededor del objeto JSON. No relaja la
 * validacion: el resultado pasa igual por `parseAIModelOutput`.
 *
 * @example extractJsonText('<think>...</think>```json\n{"kind":"actions"}\n```') // '{"kind":"actions"}'
 */
export function extractJsonText(content: string): string {
  const text = content.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
  // Razonamiento sin cerrar (respuesta cortada): no se rescata ningun borrador de JSON.
  if (/<think>/i.test(text)) return text;
  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(text);
  const body = fenced?.[1] ?? text;
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  return start >= 0 && end > start ? body.slice(start, end + 1) : body;
}

/** Extrae el texto de la respuesta segun la forma documentada de cada proveedor. */
export function extractProviderContent(kind: RemoteProviderKind, response: unknown): string | null {
  switch (kind) {
    case 'openai':
    case 'azure-openai': {
      const content = at(response, 'choices', 0, 'message', 'content');
      return typeof content === 'string' ? content : null;
    }
    case 'azure-ai': {
      const content = at(response, 'choices', 0, 'message', 'content');
      return typeof content === 'string' ? extractJsonText(content) : null;
    }
    case 'anthropic': {
      const blocks = at(response, 'content');
      if (!Array.isArray(blocks)) return null;
      const text: unknown = blocks.find((block) => isRecord(block) && block.type === 'text');
      return isRecord(text) && typeof text.text === 'string' ? extractJsonText(text.text) : null;
    }
    case 'gemini': {
      const text = at(response, 'candidates', 0, 'content', 'parts', 0, 'text');
      return typeof text === 'string' ? text : null;
    }
    case 'n8n': {
      const output = at(response, 'output');
      if (typeof output === 'string') return output;
      return isRecord(output) ? JSON.stringify(output) : null;
    }
    case 'http': {
      const content = at(response, 'content');
      return typeof content === 'string' ? content : null;
    }
  }
}

const DISPLAY_NAMES: Readonly<Record<RemoteProviderKind, string>> = {
  openai: 'OpenAI',
  'azure-openai': 'Azure OpenAI',
  'azure-ai': 'Azure AI Foundry',
  anthropic: 'Anthropic',
  gemini: 'Google Gemini',
  n8n: 'n8n',
  http: 'HTTP generico',
};

const cancelled = (): AppError => new AppError('CANCELLED', 'Solicitud de IA cancelada.');

/** Lectura no estrechada de `aborted` (puede cambiar durante un `await`). */
export const isAborted = (signal: AbortSignal | undefined): boolean => signal?.aborted === true;

/**
 * Adaptador de proveedor remoto sin SDK: traduce la peticion del AI Core al formato del
 * proveedor y extrae el texto de su respuesta. No realiza llamadas por si mismo: delega
 * en el `AITransport` inyectado, que en las pruebas es un transporte falso.
 *
 * @example
 * const provider = createProviderAdapter({ id: 'openai', kind: 'openai', endpointId: 'ai.openai', transport });
 */
export function createProviderAdapter(config: ProviderAdapterConfig): AIProvider {
  return {
    id: config.id,
    kind: config.kind,
    displayName: config.displayName ?? DISPLAY_NAMES[config.kind],
    capabilities: {
      structuredOutput:
        config.kind === 'openai' || config.kind === 'azure-openai' || config.kind === 'gemini',
      streaming: false,
    },
    async generate(request, signal): Promise<Result<AIProviderResponse, AppError>> {
      if (isAborted(signal)) return err(cancelled());
      const body = buildProviderPayload(config.kind, request, config.model);
      const sent = await config.transport.send({ endpointId: config.endpointId, body }, signal);
      if (isAborted(signal)) return err(cancelled());
      if (!sent.ok) return sent;
      const content = extractProviderContent(config.kind, sent.value);
      if (content === null) {
        return err(
          new AppError(
            'UNAVAILABLE',
            `La respuesta de ${DISPLAY_NAMES[config.kind]} no tiene el formato esperado.`,
          ),
        );
      }
      return ok({
        providerId: config.id,
        ...(config.model === undefined ? {} : { model: config.model }),
        content,
      });
    },
  };
}

/**
 * Transporte falso para pruebas y demostraciones: responde con `handler` sin red y
 * respeta la cancelacion. Nunca debe usarse para llamar a servicios reales.
 */
export function createFakeTransport(
  handler: (request: AITransportRequest) => unknown,
  options: { readonly latencyMs?: number } = {},
): AITransport & { readonly requests: readonly AITransportRequest[] } {
  const requests: AITransportRequest[] = [];
  return {
    requests,
    send(request, signal) {
      requests.push(request);
      return new Promise((resolve) => {
        if (signal?.aborted === true) {
          resolve(err(cancelled()));
          return;
        }
        const finish = (): void => {
          signal?.removeEventListener('abort', onAbort);
          try {
            resolve(ok(handler(request)));
          } catch (error) {
            resolve(
              err(new AppError('UNAVAILABLE', 'El transporte falso fallo.', { cause: error })),
            );
          }
        };
        const timer = setTimeout(finish, options.latencyMs ?? 0);
        const onAbort = (): void => {
          clearTimeout(timer);
          resolve(err(cancelled()));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    },
  };
}
